import CustomProvider from 'components/CustomProvider';
import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import RecurlyAccountStatus from 'types/RecurlyAccountStatus';
import useUser from 'hooks/useUser';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import Link from 'components/Link';
import { toggleSubscriptionStatus } from 'api/My';
import classNames from 'classnames';
import * as styles from './AutoRenewal.module.scss';

type Props = {
  recurlyAccountStatus: RecurlyAccountStatus;
};

const messages = defineMessages({
  noTrial: {
    defaultMessage:
      'Are you sure? Your AllTrails Pro subscription will expire on {date} and will not auto-renew. If you wish to cancel your current subscription, please email support@alltrails.com.'
  },
  trial: {
    defaultMessage: 'Are you sure? Your AllTrails Pro trial will expire on {date} and will not auto-renew.'
  }
});

async function toggle(confirmText: string) {
  if (window.confirm(confirmText)) {
    await toggleSubscriptionStatus();
    window.location.reload();
  }
}

function TurnOffAutoRenew() {
  const intl = useIntl();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();

  const formatter = new Intl.DateTimeFormat(languageRegionCode, { day: 'numeric', month: 'long', year: 'numeric' });

  const prompt = intl.formatMessage(user.subscription?.trial ? messages.trial : messages.noTrial, {
    date: formatter.format(new Date(user.expirationDate))
  });

  return (
    <li className="events">
      <button
        type="button"
        onClick={() => {
          toggle(prompt);
        }}
        className={styles.button}
      >
        <FormattedMessage defaultMessage="Turn Off Auto-Renew" />
      </button>
    </li>
  );
}

function CancelSubscription() {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();

  const formatter = new Intl.DateTimeFormat(languageRegionCode, { day: 'numeric', month: 'long', year: 'numeric' });

  const prompt = intl.formatMessage(
    defineMessages({
      message: {
        defaultMessage:
          'Are you sure? Your AllTrails Pro subscription expired on {date} and has not been renewed. If you wish to continue your subscription, please update your Billing Info.'
      }
    }).message,
    {
      date: formatter.format(new Date(user.expirationDate))
    }
  );

  return (
    <li className="events">
      <button
        onClick={() => {
          toggle(prompt);
        }}
        type="button"
        className={styles.button}
      >
        <FormattedMessage defaultMessage="Cancel Subscription" />
      </button>
    </li>
  );
}

function AutoRenewalContent({ recurlyAccountStatus }: Props) {
  switch (recurlyAccountStatus) {
    case RecurlyAccountStatus.Active:
      return <TurnOffAutoRenew />;
    case RecurlyAccountStatus.PastDue:
      return <CancelSubscription />;
    case RecurlyAccountStatus.Cancelled:
      return (
        <Link
          href="/my/profile/toggle_subscription_status"
        >
          <li className={classNames('events', styles.li)}>
            <FormattedMessage defaultMessage="Turn On Auto-Renew" />
          </li>
        </Link>
      );
    default:
      return null;
  }
}

function AutoRenewal({ recurlyAccountStatus }: Props) {
  return (
    <CustomProvider>
      <AutoRenewalContent recurlyAccountStatus={recurlyAccountStatus} />
    </CustomProvider>
  );
}

export default AutoRenewal;
